/* Sweet Nabbody — couche de mouvement.

   Trois custom elements, rien d'autre. Un custom element se reinstancie tout seul
   quand Shopify reinjecte le HTML d'une section dans l'editeur de theme : c'est ce
   qui manquait a la v2, dont les modules se branchaient une seule fois au
   DOMContentLoaded et cassaient a chaque edition.

   <mvt-reveler>  revele ses enfants au defilement, en cascade
   <mvt-recit>    scrollytelling : une colonne collante, des vues qui s'activent
   <mvt-hero>     revele le titre ligne par ligne, puis la phrase, puis le bouton

   Aucun de ces elements n'est necessaire a la lecture : sans JavaScript, le
   contenu s'affiche normalement, seul le mouvement manque. */

const DOUX = matchMedia('(prefers-reduced-motion: reduce)');

/* Revelation au defilement. L'element observe ses enfants directs, ou les
   elements designes par son attribut `cible`. */
class MvtReveler extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;              // deja branche
    this.setAttribute('monte', '');
    const sel = this.getAttribute('cible');
    const cibles = sel ? [...this.querySelectorAll(sel)] : [...this.children];
    if (!cibles.length) return;

    if (DOUX.matches || !('IntersectionObserver' in window)) {
      cibles.forEach((c) => c.classList.add('est-visible'));
      return;
    }

    const pas = parseInt(this.getAttribute('cascade') || '80', 10);
    cibles.forEach((c) => c.setAttribute('data-revele', ''));

    this.observateur = new IntersectionObserver((entrees, obs) => {
      entrees.forEach((e) => {
        if (!e.isIntersecting) return;
        const i = cibles.indexOf(e.target);
        e.target.style.setProperty('--retard', `${Math.max(0, i) * pas}ms`);
        e.target.classList.add('est-visible');
        // On relache will-change une fois la transition finie.
        e.target.addEventListener('transitionend', () => e.target.classList.add('est-fini'),
                                  { once: true });
        obs.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });

    cibles.forEach((c) => this.observateur.observe(c));
  }
  disconnectedCallback() { this.observateur?.disconnect(); }
}

/* Scrollytelling. Les vues s'activent au passage, la colonne de texte est collee
   par le CSS. Sur mobile, la grille tombe et chaque etape se revele simplement. */
class MvtRecit extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    const etapes = [...this.querySelectorAll('[data-recit-etape]')];
    const vues = [...this.querySelectorAll('[data-recit-vue]')];
    if (!etapes.length || !vues.length) return;

    if (DOUX.matches || !('IntersectionObserver' in window)) {
      vues.forEach((v) => v.classList.add('est-active'));
      return;
    }
    vues[0].classList.add('est-active');

    this.observateur = new IntersectionObserver((entrees) => {
      entrees.forEach((e) => {
        if (!e.isIntersecting) return;
        const i = etapes.indexOf(e.target);
        vues.forEach((v, j) => v.classList.toggle('est-active', j === i));
      });
    }, { rootMargin: '-45% 0px -45% 0px' });

    etapes.forEach((e) => this.observateur.observe(e));
  }
  disconnectedCallback() { this.observateur?.disconnect(); }
}

/* Le hero. Le titre monte ligne par ligne, puis la phrase, puis le bouton.
   L'entree se declenche a la premiere frame utile, pas au chargement complet :
   attendre `load` ferait apparaitre le titre plusieurs secondes trop tard. */
class MvtHero extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    const hero = this.querySelector('.hero') || this;
    if (DOUX.matches) { hero.classList.add('est-prete'); return; }

    const lignes = [...this.querySelectorAll('.hero__ligne>span')];
    const suite = [...this.querySelectorAll('.hero__phrase,.hero__action')];
    hero.setAttribute('data-anime', '');        // c'est ici que le masquage commence
    lignes.forEach((l, i) => l.style.setProperty('--retard', `${i * 120}ms`));
    suite.forEach((s, i) => s.style.setProperty('--retard', `${lignes.length * 120 + i * 140}ms`));

    requestAnimationFrame(() => requestAnimationFrame(() => hero.classList.add('est-prete')));

    // Parallaxe : 0.15, uniquement tant que le hero est a l'ecran, et jamais sur
    // un appareil qui prefere le repos.
    const media = this.querySelector('.hero__media');
    if (!media || !('IntersectionObserver' in window)) return;
    let visible = false, attendu = false;
    const bouge = () => {
      attendu = false;
      const y = Math.min(window.scrollY, innerHeight);
      media.style.transform = `translate3d(0, ${y * 0.15}px, 0)`;
    };
    this.observateur = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (!visible) media.style.transform = '';
    });
    this.observateur.observe(this);
    this.surDefilement = () => {
      if (!visible || attendu) return;
      attendu = true; requestAnimationFrame(bouge);
    };
    addEventListener('scroll', this.surDefilement, { passive: true });
  }
  disconnectedCallback() {
    this.observateur?.disconnect();
    if (this.surDefilement) removeEventListener('scroll', this.surDefilement);
  }
}

customElements.define('mvt-reveler', MvtReveler);
customElements.define('mvt-recit', MvtRecit);
customElements.define('mvt-hero', MvtHero);

/* ------------------------------------------------------- Video du hero
   La video ne doit rien couter au premier rendu : c'est le poster qui est
   l'image LCP. Les <source> arrivent donc sans `src`, et on ne les renseigne
   qu'une fois la page chargee et le navigateur au repos. Tant que ce moment
   n'est pas venu, ou si le visiteur demande moins de mouvement ou navigue en
   economie de donnees, le poster reste et rien n'est telecharge. */
class MvtHeroVideo extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    this.video = this.querySelector('video');
    if (!this.video) return;

    const eco = navigator.connection && (navigator.connection.saveData ||
                /2g/.test(navigator.connection.effectiveType || ''));
    if (DOUX.matches || eco) return;                       // le poster suffit

    const partir = () => {
      const sources = [...this.video.querySelectorAll('source[data-src]')];
      if (!sources.length) return;
      sources.forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); });
      this.video.load();
      this.video.play().then(() => this.classList.add('joue')).catch(() => {});
    };
    const auRepos = () => (window.requestIdleCallback
      ? requestIdleCallback(partir, { timeout: 2500 }) : setTimeout(partir, 900));
    if (document.readyState === 'complete') auRepos();
    else addEventListener('load', auRepos, { once: true });

    const pause = this.querySelector('[data-hero-pause]');
    pause?.addEventListener('click', () => {
      const arrete = this.video.paused;
      arrete ? this.video.play() : this.video.pause();
      pause.setAttribute('aria-label', arrete ? 'Mettre la video en pause' : 'Reprendre la video');
      this.classList.toggle('en-pause', !arrete);
    });
  }
}
customElements.define('mvt-hero-video', MvtHeroVideo);

/* ---------------------------------------------------------------- Galerie
   Vignettes, glissement, et sur desktop le plein ecran au clic. Le pincement
   mobile est laisse au navigateur : une modale de zoom maison est toujours plus
   pauvre que le zoom natif, et elle se met en travers du geste appris. */
class MvtGalerie extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    this.piste = this.querySelector('[data-galerie-piste]');
    this.vues = [...this.querySelectorAll('.galerie__vue')];
    const barre = this.parentElement.querySelector('[data-galerie-vignettes]');
    this.vignettes = barre ? [...barre.querySelectorAll('.vignette')] : [];
    if (!this.piste || !this.vues.length) return;

    this.vignettes.forEach((v, i) => {
      v.addEventListener('click', () => this.aller(i));
    });

    // La vignette active suit le defilement du carrousel.
    if (matchMedia('(max-width:989px)').matches) {
      const io = new IntersectionObserver((entrees) => {
        entrees.forEach((e) => {
          if (e.isIntersecting) this.marquer(this.vues.indexOf(e.target));
        });
      }, { root: this.piste, threshold: 0.6 });
      this.vues.forEach((v) => io.observe(v));
    }
  }

  aller(i) {
    const cible = this.vues[i];
    if (!cible) return;
    if (matchMedia('(max-width:989px)').matches) {
      this.piste.scrollTo({ left: cible.offsetLeft, behavior: 'smooth' });
    } else {
      cible.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    this.marquer(i);
  }

  marquer(i) {
    this.vignettes.forEach((v, k) =>
      v.setAttribute('aria-current', String(k === i)));
  }
}
customElements.define('mvt-galerie', MvtGalerie);

/* ------------------------------------------------------------- Avant/apres
   Le voile n'est pose qu'ici : sans script, l'image entiere reste visible avec
   ses deux moities. La preuve ne depend jamais du JavaScript. */
class MvtComparer extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    const curseur = this.querySelector('.comparer__curseur');
    if (!curseur) return;
    this.dataset.actif = '';
    const poser = () => this.style.setProperty('--coupe', `${curseur.value}%`);
    poser();
    curseur.addEventListener('input', poser);

    // Glissement au doigt et a la souris directement sur l'image : le curseur
    // sous l'image reste la commande accessible, mais personne ne pense a
    // l'utiliser quand la photo elle-meme invite a etre balayee.
    const cadre = this.querySelector('.comparer__cadre');
    if (!cadre) return;
    const suivre = (e) => {
      const r = cadre.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
      curseur.value = String(Math.max(0, Math.min(100, (x / r.width) * 100)));
      poser();
    };
    let actif = false;
    const debut = (e) => { actif = true; suivre(e); };
    const fin = () => { actif = false; };
    cadre.addEventListener('pointerdown', debut);
    cadre.addEventListener('pointermove', (e) => { if (actif) suivre(e); });
    addEventListener('pointerup', fin);
    addEventListener('pointercancel', fin);
  }
}
customElements.define('mvt-comparer', MvtComparer);


/* ===========================================================================
   EN-TÊTE : annonce qui alterne, menu à panneaux.        Points 3.1 et 4.
   ===========================================================================

   Le balisage livre TOUT : les trois messages, les six entrées, toutes les
   sous-entrées, dans le flux. Sans ce script, la page est une liste de liens
   complète et lisible. Le script ne fait que replier ce qui peut l'être.

   C'est la règle 3 de la maison : l'état caché est posé ici, jamais dans le
   balisage ni dans le CSS de départ. `data-monte` en est la preuve, et le CSS
   n'agit que sous cet attribut.
*/
class MvtEntete extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');

    this.env = this.querySelector('[data-menu-enveloppe]');   /* le <details> */
    this.menu = this.querySelector('[data-menu]');
    this.bouton = this.querySelector('[data-ouvrir-menu]');   /* le <summary> */
    this.p2 = [...this.querySelectorAll('[data-niveau="2"]')];

    this.annonce();
    this.replier();
    this.brancher();
    this.header();
  }

  /* --- 3.1 · trois messages, un fondu toutes les six secondes -------------- */
  annonce() {
    const mots = [...this.querySelectorAll('[data-annonce-mot]')];
    if (mots.length < 2) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let i = 0;
    mots.forEach((m, k) => { m.style.opacity = k === 0 ? '1' : '0'; });
    this.horloge = setInterval(() => {
      mots[i].style.opacity = '0';
      i = (i + 1) % mots.length;
      mots[i].style.opacity = '1';
    }, 6000);
  }

  /* --- 4 · le menu ---------------------------------------------------------
     Le <details> porte l'état ouvert ou fermé. Le script ne le REPLIE plus :
     il est déjà fermé par le HTML, et c'est ce qui a supprimé le décalage de
     mise en page. Il ne reste ici que les panneaux de niveau 2, qui eux sont
     dans le flux du niveau 1 et n'ont pas de fermeture native. */
  replier() {
    this.p2.forEach((p) => { p.hidden = true; });
  }

  brancher() {
    /* Le <details> s'ouvre tout seul au clic sur le <summary>. On écoute son
       changement d'état plutôt que le clic : au clavier, à la souris ou par une
       recherche dans la page, c'est le même événement. */
    this.env.addEventListener('toggle', () => {
      if (this.env.open) this.ouvrir(); else this.fermer();
    });
    this.querySelectorAll('[data-fermer-menu]').forEach(
      (b) => b.addEventListener('click', () => this.fermer()));
    this.querySelectorAll('[data-ouvrir-niveau2]').forEach((b) => {
      b.addEventListener('click', () => this.niveau2(b.dataset.ouvrirNiveau2, b));
    });
    this.querySelectorAll('[data-retour-niveau1]').forEach(
      (b) => b.addEventListener('click', () => this.niveau1()));

    /* Échap ferme le panneau ouvert, puis le menu. Deux gestes, deux effets :
       une seule touche qui referme tout d'un coup fait perdre sa place. */
    this.surTouche = (e) => {
      if (e.key !== 'Escape' || this.menu.hidden) return;
      const ouvert = this.p2.find((p) => !p.hidden);
      if (ouvert) this.niveau1(); else this.fermer();
    };
    addEventListener('keydown', this.surTouche);
  }

  ouvrir() {
    /* Le repaint doit avoir lieu AVANT la classe qui anime, sinon le panneau
       apparaît en place au lieu de glisser. */
    requestAnimationFrame(() => this.menu.classList.add('est-ouvert'));
    document.documentElement.style.overflow = 'hidden';
    const cible = this.menu.querySelector('.menu__croix, .menu__entree');
    if (cible) cible.focus();
  }

  /* `fermer` est appelée par le `toggle` quand le <details> se referme, et par
     la croix, le voile et Échap, qui le referment eux-mêmes. */
  fermer() {
    if (this.env.open) { this.env.open = false; return; }
    this.niveau1();
    this.menu.classList.remove('est-ouvert');
    document.documentElement.style.overflow = '';
    this.bouton.focus();
  }

  niveau2(id, bouton) {
    this.p2.forEach((p) => {
      const pour = p.dataset.pour === id;
      p.hidden = !pour;
      if (pour) requestAnimationFrame(() => p.classList.add('est-ouvert'));
      else p.classList.remove('est-ouvert');
    });
    this.querySelectorAll('[data-ouvrir-niveau2]').forEach(
      (b) => b.setAttribute('aria-expanded', String(b === bouton)));
    const p = this.p2.find((x) => x.dataset.pour === id);
    const r = p && p.querySelector('[data-retour-niveau1]');
    if (r) r.focus();
  }

  niveau1() {
    this.p2.forEach((p) => { p.classList.remove('est-ouvert'); p.hidden = true; });
    this.querySelectorAll('[data-ouvrir-niveau2]').forEach(
      (b) => b.setAttribute('aria-expanded', 'false'));
  }

  /* --- 3.1 · le header transparent se pose au premier défilement ----------- */
  header() {
    if (!this.classList.contains('entete--transparent')) return;
    const poser = () => this.classList.toggle('est-pose', scrollY > 8);
    poser();
    addEventListener('scroll', poser, { passive: true });
  }

  disconnectedCallback() {
    if (this.horloge) clearInterval(this.horloge);
    if (this.surTouche) removeEventListener('keydown', this.surTouche);
  }
}
customElements.define('mvt-entete', MvtEntete);


/* ===========================================================================
   CARROUSEL : une piste qui défile, des points, deux flèches.
   ===========================================================================

   Le défilement est celui du navigateur, avec `scroll-snap`. Rien n'est
   déplacé en JavaScript : les flèches appellent `scrollTo`, les points aussi,
   et le doigt fait ce qu'il a toujours fait. Sans ce script, la piste reste une
   liste qui défile horizontalement, ce qui est déjà utilisable.

   Aucun défilement automatique : le cahier l'interdit, et il a raison.
*/
class MvtCarrousel extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    this.piste = this.querySelector('[data-piste]');
    if (!this.piste) return;
    this.vues = [...this.piste.children];
    if (this.vues.length < 2) return;

    this.points = this.querySelector('[data-points]');
    this.avant = this.querySelector('[data-avant]');
    this.apres = this.querySelector('[data-apres]');

    if (this.points) {
      this.vues.forEach((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'carrousel__point';
        b.setAttribute('aria-label', `Aller à l'élément ${i + 1} sur ${this.vues.length}`);
        b.addEventListener('click', () => this.aller(i));
        this.points.appendChild(b);
      });
    }
    if (this.avant) this.avant.addEventListener('click', () => this.decaler(-1));
    if (this.apres) this.apres.addEventListener('click', () => this.decaler(1));

    this.piste.addEventListener('scroll', () => {
      clearTimeout(this.attente);
      this.attente = setTimeout(() => this.majEtat(), 90);
    }, { passive: true });
    this.majEtat();
  }

  /* L'élément le plus proche du bord gauche de la piste. */
  rang() {
    const g = this.piste.getBoundingClientRect().left;
    let meilleur = 0, ecart = Infinity;
    this.vues.forEach((v, i) => {
      const d = Math.abs(v.getBoundingClientRect().left - g);
      if (d < ecart) { ecart = d; meilleur = i; }
    });
    return meilleur;
  }

  aller(i) {
    const v = this.vues[Math.max(0, Math.min(this.vues.length - 1, i))];
    this.piste.scrollTo({ left: v.offsetLeft - this.piste.offsetLeft, behavior: 'smooth' });
  }

  decaler(n) { this.aller(this.rang() + n); }

  majEtat() {
    const i = this.rang();
    if (this.points) {
      [...this.points.children].forEach((b, k) => {
        b.classList.toggle('est-actif', k === i);
        b.setAttribute('aria-current', k === i ? 'true' : 'false');
      });
    }
    /* Les flèches se désactivent aux extrémités : un bouton qui ne fait rien
       doit le dire, sinon on clique trois fois avant de comprendre. */
    const fin = this.piste.scrollWidth - this.piste.clientWidth;
    if (this.avant) this.avant.disabled = this.piste.scrollLeft <= 2;
    if (this.apres) this.apres.disabled = this.piste.scrollLeft >= fin - 2;
  }
}
customElements.define('mvt-carrousel', MvtCarrousel);


/* ===========================================================================
   MOTS TOURNANTS du titre de marque.                          Point 3.3.
   ===========================================================================
   Les trois mots sont dans le balisage, donc dans le HTML servi, donc lus par
   les moteurs et par les lecteurs d'écran. Le script n'en montre qu'un à la
   fois ; sans lui, ils sont tous là, à la suite, et la phrase reste vraie.
*/
class MvtMots extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');
    const mots = [...this.querySelectorAll('[data-mot]')];
    if (mots.length < 2) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    /* L'état empilé est posé ICI : hors script, les mots restent dans le flux. */
    this.setAttribute('empile', '');
    let i = 0;
    mots.forEach((m, k) => { m.style.opacity = k === 0 ? '1' : '0'; });
    this.horloge = setInterval(() => {
      mots[i].style.opacity = '0';
      i = (i + 1) % mots.length;
      mots[i].style.opacity = '1';
    }, 2500);
  }
  disconnectedCallback() { if (this.horloge) clearInterval(this.horloge); }
}
customElements.define('mvt-mots', MvtMots);


/* ===========================================================================
   BANDEAU DIAGNOSTIC COLLANT.                                 Point 3.14.
   ===========================================================================
   Trois raisons de se taire, et une mémoire de sept jours.

   `localStorage` peut lever : navigation privée, cookies bloqués, iframe
   d'aperçu. Toute lecture et toute écriture sont donc protégées, et en cas
   d'échec le bandeau s'affiche — mieux vaut un bandeau de trop qu'une page qui
   plante.
*/
class MvtBandeau extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('monte')) return;
    this.setAttribute('monte', '');

    if (this.ferme()) { this.hidden = true; return; }

    this.querySelector('[data-fermer-bandeau]')
        .addEventListener('click', () => this.fermer());

    /* La barre d'achat d'une fiche et le menu ouvert font taire le bandeau. On
       les observe plutôt que de les deviner : la barre apparaît au défilement,
       le menu à un clic, et aucun des deux ne prévient. */
    this.observer = new MutationObserver(() => this.arbitrer());
    const barre = document.querySelector('[data-barre-achat], .barre-achat');
    const menu = document.querySelector('[data-menu]');
    [barre, menu].forEach((n) => {
      if (n) this.observer.observe(n, { attributes: true, attributeFilter: ['class', 'hidden'] });
    });
    this.barre = barre; this.menu = menu;
    addEventListener('scroll', () => this.arbitrer(), { passive: true });
    this.arbitrer();
  }

  arbitrer() {
    const barreVisible = this.barre && !this.barre.hidden
      && this.barre.getBoundingClientRect().height > 0
      && getComputedStyle(this.barre).visibility !== 'hidden';
    const menuOuvert = this.menu && !this.menu.hidden;
    this.classList.toggle('est-tu', Boolean(barreVisible || menuOuvert));
  }

  ferme() {
    try {
      const t = Number(localStorage.getItem('snb-bandeau-diag') || 0);
      return t > 0 && (Date.now() - t) < 7 * 24 * 3600 * 1000;
    } catch (e) { return false; }
  }

  fermer() {
    try { localStorage.setItem('snb-bandeau-diag', String(Date.now())); } catch (e) { /* rien */ }
    this.hidden = true;
  }

  disconnectedCallback() { if (this.observer) this.observer.disconnect(); }
}
customElements.define('mvt-bandeau', MvtBandeau);
